import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const EXIT_TYPES = [
  'voluntary_resignation', 'retirement', 'mutual_agreement', 'termination',
  'contract_end', 'health_reasons', 'military_service', 'woman_marriage_4857_14',
];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOdAlumniApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-alumni?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const revokeConsent = useCallback(async (id, reason) => {
    try { await api.post(`/v1/offboarding-alumni/${id}/revoke-consent`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const setBoomerangBlock = useCallback(async (id, block, reason) => {
    try { await api.post(`/v1/offboarding-alumni/${id}/boomerang-block`, { block, reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const exitTypeLabel = (s) => t(`offboarding.exitType.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboardingAlumni.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'personEmail',
        label: t('offboardingAlumni.email'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personEmail[like]' },
        },
      },
      {
        name: 'exitType',
        label: t('offboardingAlumni.exitTypeLabel'),
        options: {
          ...multiSelectFilter(t('offboardingAlumni.exitTypeLabel'), EXIT_TYPES, exitTypeLabel),
          customBodyRender: (v) => exitTypeLabel(v),
        },
      },
      dateFilterColumn('exitDate', t('offboardingAlumni.exitDate'), lang),
      {
        name: 'boomerangBlocked',
        label: t('offboardingAlumni.boomerangBlocked'),
        options: {
          filter: true, sort: true,
          customBodyRender: (v) => v ? t('common.yes') : t('common.no'),
        },
      },
      {
        name: 'isActive',
        label: t('offboardingAlumni.consentActive'),
        options: {
          filter: true, sort: true,
          customBodyRender: (v) => v ? t('common.yes') : t('common.no'),
        },
      },
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, revokeConsent, setBoomerangBlock };
};

export default useOdAlumniApi;
